import {
  AtlasButtonProps,
  AtlasMenuContentItem,
  Button,
  Icon,
  Menu,
} from "@resource/atlas";
import { atlasCheck, atlasEdit2 } from "@resource/atlas/icons";
import { useLogEvent } from "analytics";
import clsx from "clsx";
import { MouseEventHandler, useMemo } from "react";
import { PersistableCalendarEvent } from "shared/guide-content/rich-blocks/calendar-utils/types";

import { RequestAvailabilityInfo } from "./RequestAvailabilityInfo";

// component
// ---------

export type SenderRequestAvailabilityButtonProps = {
  availabilitySubmitted?: boolean;
  onEdit(): void;
};

function SenderRequestAvailabilityButton({
  availabilitySubmitted,
  onEdit,
}: SenderRequestAvailabilityButtonProps) {
  const logEvent = useLogEvent({
    component: "SenderRequestAvailabilityButton",
    project: "Request Availability From A Candidate",
  });
  const menuOptions = useMemo(
    (): AtlasMenuContentItem[] => [
      {
        key: "edit",
        _type: "item",
        children: "Edit",
        disabled: availabilitySubmitted,
        leadingContent: <Icon content={atlasEdit2} />,
        onSelect: () => {
          onEdit();
          logEvent("Edit Availability Request Clicked");
        },
      },
    ],
    [onEdit, availabilitySubmitted, logEvent]
  );

  return (
    <div>
      <Menu.Root>
        <Menu.Trigger>
          <Button
            isDropdown
            className="rounded-full"
            variant={availabilitySubmitted ? "success" : "default"}
            icon={availabilitySubmitted ? atlasCheck : undefined}
          >
            {availabilitySubmitted
              ? "Availability shared"
              : "Waiting for response"}
          </Button>
        </Menu.Trigger>
        <Menu.Content items={menuOptions} />
      </Menu.Root>
    </div>
  );
}

export type RecipientRequestAvailabilityButtonProps = AtlasButtonProps & {
  availabilitySubmitted?: boolean;
};

function RecipientRequestAvailabilityButton({
  availabilitySubmitted,
  className,
  onClick,
  ...props
}: RecipientRequestAvailabilityButtonProps) {
  const logEvent = useLogEvent({
    component: "RecipientRequestAvailabilityButton",
    project: "Request Availability From A Candidate",
  });
  return (
    <Button
      variant={availabilitySubmitted ? "success" : "primary"}
      icon={availabilitySubmitted ? atlasCheck : undefined}
      className="rounded-full"
      disabled={availabilitySubmitted}
      onClick={(e) => {
        onClick?.(e);
        logEvent("Share Availability Clicked");
      }}
      {...props}
    >
      {availabilitySubmitted ? "Availability shared" : "Share availability"}
    </Button>
  );
}

type BaseRequestAvailabilityButtonProps = {
  className?: string;
  title: string;
  description: string;
  duration: number;
  organization?: {
    name: string;
    avatarUrl: string;
  };
  candidate?: {
    name: string;
  };
  availabilitySubmitted?: boolean;
  onRequestAvailabilityButtonClick?: MouseEventHandler<HTMLButtonElement>;
  onEditSenderSuggestions?(): void;
  isRecipient: boolean;
};

type ViewRequestAvailabilityButtonProps = BaseRequestAvailabilityButtonProps & {
  isEditing?: false | undefined;
};

type EditRequestAvailabilityButtonProps = BaseRequestAvailabilityButtonProps & {
  isEditing: true;
  timezone: string;
  senderSuggestions: PersistableCalendarEvent[];
  onEdit(): void;
};

type RequestAvailabilityDisplayProps =
  | ViewRequestAvailabilityButtonProps
  | EditRequestAvailabilityButtonProps;

export function RequestAvailabilityDisplay(
  props: RequestAvailabilityDisplayProps
) {
  const {
    className,
    availabilitySubmitted,
    onRequestAvailabilityButtonClick,
    onEditSenderSuggestions,
    ...rest
  } = props;

  return (
    <>
      <div className={clsx("p-4 shadow-1 rounded-md space-y-4", className)}>
        <div className="space-y-3">
          <RequestAvailabilityInfo {...rest} />
        </div>
        {!rest.isEditing &&
          (rest.isRecipient ? (
            <RecipientRequestAvailabilityButton
              availabilitySubmitted={availabilitySubmitted}
              onClick={onRequestAvailabilityButtonClick}
            />
          ) : (
            <SenderRequestAvailabilityButton
              availabilitySubmitted={availabilitySubmitted}
              onEdit={() => onEditSenderSuggestions?.()}
            />
          ))}
      </div>
    </>
  );
}
